import { useEffect, useState, useCallback } from 'react';

// Mobile detection and optimization hook
export const useMobileOptimization = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [touchSupport, setTouchSupport] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  // Device detection
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setOrientation(width > height ? 'landscape' : 'portrait');
      setTouchSupport('ontouchstart' in window);
      setViewportHeight(height);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    window.addEventListener('orientationchange', checkDevice);

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  // Keyboard detection for mobile
  useEffect(() => {
    if (!isMobile) return;

    const initialHeight = window.innerHeight;
    
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDifference = initialHeight - currentHeight;
      
      // Keyboard is likely visible if height decreased by more than 150px
      setKeyboardVisible(heightDifference > 150);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Optimize touch interactions
  const optimizeTouchTarget = useCallback((element: HTMLElement) => {
    if (!touchSupport) return;

    // Ensure minimum touch target size (44x44px)
    const style = element.style;
    const computedStyle = window.getComputedStyle(element);
    
    if (parseInt(computedStyle.minHeight) < 44) {
      style.minHeight = '44px';
    }
    if (parseInt(computedStyle.minWidth) < 44) {
      style.minWidth = '44px';
    }
    
    // Improve touch responsiveness
    style.touchAction = 'manipulation';
    style.userSelect = 'none';
  }, [touchSupport]);

  // Prevent zoom on input focus (iOS)
  const preventZoomOnFocus = useCallback(() => {
    if (!isMobile) return;

    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }, [isMobile]);

  // Safe area utilities for notched devices
  const getSafeAreaInsets = useCallback(() => {
    const style = getComputedStyle(document.documentElement);
    return {
      top: style.getPropertyValue('env(safe-area-inset-top)') || '0px',
      right: style.getPropertyValue('env(safe-area-inset-right)') || '0px',
      bottom: style.getPropertyValue('env(safe-area-inset-bottom)') || '0px',
      left: style.getPropertyValue('env(safe-area-inset-left)') || '0px',
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    isIPad: isTablet && /iPad/.test(navigator.userAgent),
    isTouchDevice: touchSupport,
    orientation,
    touchSupport,
    viewportHeight,
    screenWidth: window.innerWidth,
    keyboardVisible,
    optimizeTouchTarget,
    preventZoomOnFocus,
    getSafeAreaInsets,
    // Utility classes for conditional rendering
    mobileOnly: isMobile ? '' : 'hidden',
    tabletOnly: isTablet ? '' : 'hidden',
    desktopOnly: !isMobile && !isTablet ? '' : 'hidden',
    touchDevice: touchSupport ? '' : 'hidden',
    nonTouchDevice: !touchSupport ? '' : 'hidden'
  };
};

// Hook for responsive breakpoints
export const useResponsiveBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState<'sm' | 'md' | 'lg' | 'xl' | '2xl'>('sm');

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width >= 1536) setBreakpoint('2xl');
      else if (width >= 1280) setBreakpoint('xl');
      else if (width >= 1024) setBreakpoint('lg');
      else if (width >= 768) setBreakpoint('md');
      else setBreakpoint('sm');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  return {
    breakpoint,
    isSm: breakpoint === 'sm',
    isMd: breakpoint === 'md',
    isLg: breakpoint === 'lg',
    isXl: breakpoint === 'xl',
    is2Xl: breakpoint === '2xl',
    isSmUp: ['sm', 'md', 'lg', 'xl', '2xl'].includes(breakpoint),
    isMdUp: ['md', 'lg', 'xl', '2xl'].includes(breakpoint),
    isLgUp: ['lg', 'xl', '2xl'].includes(breakpoint),
    isXlUp: ['xl', '2xl'].includes(breakpoint)
  };
};